import React, { useState, useEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import isFunction from 'lodash/isFunction';
import isUndefined from 'lodash/isUndefined';
import { citationLiteratureFormatter } from '@Services/FormatUtils';
import externalUrl from '@Utils/ExternalUrl';
import InnerHTML from '@Components/InnerHTML';
import { MetadataPropTypesShape } from '@Components/Results/MetadataPropTypes';
import Catalyst from '@Services/Catalyst';
import { getFirstAvailableSection, getDetailNavDataE2e } from '@Components/Results/documents/DocumentExpandSections';
import DocumentButtons from '@Components/Results/documents/DocumentButtons';
import DocumentCollapsibleContent from '@Components/Results/documents/DocumentCollapsibleContent';
import useExclusiveDetails from '@Components/Results/common/useExclusiveDetails';
import InSummaryPillContainer from '@Containers/Summary/InSummaryPillContainer';

import ViewFullPatentFamily from '@Containers/PatentFamilyClustering/Components/ViewFullPatentFamily';
import PatentContentContext from './PatentContentContext';
import PatentTitleMainFamilyMember from './PatentTitleMainFamilyMember';
import PatentTitleFamilyMember from './PatentTitleFamilyMember';
import PatentTitleSinglePatent from './PatentTitleSinglePatent';
import ProcessingModeWrapper from './ProcessingModeWrapper';

function PatentContent({
  document,
  metadata,
  zoom,
  isMainFamilyMember,
  isSinglePatent,
  getTrackingAttrs,
  firstSectionExpanded,
  setFirstSectionExpanded,
  viewFullPatentFamily,
}) {
  const firstExpandSection = useMemo(() => getFirstAvailableSection(document), [document]);
  // Exclusive details navigation: at most one category open at a time. When
  // nothing is open, the matched-text hit cards render as the default view.
  const { activeId, toggle, open, close } = useExclusiveDetails();
  const [updatedCitedByUrl, setUpdatedCitedByUrl] = useState('');
  const navRef = useRef(null);

  const parentControlsFirstSection = isFunction(setFirstSectionExpanded);

  const focusNavItem = (id) => {
    const navDataE2e = getDetailNavDataE2e(id);
    if (navDataE2e) {
      const navTarget = navRef.current?.querySelector(`[data-e2e="${navDataE2e}"]`);
      (navTarget?.closest('button') || navTarget)?.focus();
    }
  };

  // The first section of the main family member is controlled by the parent
  // (clicking the family group title). Bridge that into the exclusive state.
  useEffect(() => {
    if (isUndefined(firstSectionExpanded)) return;
    if (firstSectionExpanded) {
      open(firstExpandSection);
    } else if (activeId === firstExpandSection) {
      close();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firstSectionExpanded]);

  const handleToggle = (id) => {
    // The first section is controlled by the parent; let it drive the state.
    if (id === firstExpandSection && parentControlsFirstSection) {
      setFirstSectionExpanded(activeId !== firstExpandSection);
      return;
    }

    // Switching to another section closes the parent-controlled first section too.
    if (parentControlsFirstSection && activeId === firstExpandSection) {
      setFirstSectionExpanded(false);
    }
    toggle(id);
  };

  const handleOpen = (id) => {
    if (id === firstExpandSection && parentControlsFirstSection) {
      setFirstSectionExpanded(true);
      return;
    }

    if (parentControlsFirstSection && activeId === firstExpandSection) {
      setFirstSectionExpanded(false);
    }
    open(id);
  };

  const handleNavBlurClose = (id) => {
    if (id === firstExpandSection && parentControlsFirstSection) {
      setFirstSectionExpanded(false);
      return;
    }

    close();
  };

  const handleClose = () => {
    const closingId = activeId;
    if (parentControlsFirstSection && activeId === firstExpandSection) {
      setFirstSectionExpanded(false);
    } else {
      close();
    }
    focusNavItem(closingId);
  };

  const onTitleClick = (event) => {
    if (event) {
      event.preventDefault();
    }
    handleToggle(firstExpandSection);
  };

  useEffect(() => {
    Catalyst.appendVisitorIDToUrl(externalUrl(document.citedBy)).then(setUpdatedCitedByUrl);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { title: documentTitle } = document;
  const patentTitle = citationLiteratureFormatter(document.citationPatents);

  const contextValue = useMemo(
    () => ({
      document,
      updatedCitedByUrl,
      getTrackingAttrs,
    }),
    [document, updatedCitedByUrl, getTrackingAttrs]
  );

  // Extracted from nested ternary to improve readability
  const titleElement = (() => {
    if (isSinglePatent) {
      return <PatentTitleSinglePatent title={documentTitle} onClick={onTitleClick} getTrackingAttrs={getTrackingAttrs} />;
    }

    if (isMainFamilyMember) {
      return <PatentTitleMainFamilyMember title={patentTitle} />;
    }

    return <PatentTitleFamilyMember title={documentTitle} onClick={onTitleClick} getTrackingAttrs={getTrackingAttrs} />;
  })();

  return (
    <PatentContentContext.Provider value={contextValue}>
      <div className="patent-content u-els-margin-left u-els-font-size-meta">
        <div className="patent-content__element">
          {titleElement}
          <InSummaryPillContainer
            citation={document}
            databaseId={metadata?.databaseId}
            resultName={metadata?.resultName}
            className="u-els-margin-top-1o4 u-els-margin-bottom-1o2"
          />
          {!isMainFamilyMember && (
            <ProcessingModeWrapper>
              <InnerHTML tag="div" className="rx-element-literature" html={patentTitle} />
            </ProcessingModeWrapper>
          )}
          {document.assignees && (
            <div className="patent-content__current-patent-assignee">
              <span>Current Patent Assignee: </span>
              <InnerHTML tag="span" className="rx-element-assignee" html={document.assignees} />
            </div>
          )}
          <div className="patent-content__offices-row">
            {document.citationPatents && (
              <div>
                <span>{`Office${document.citationPatents.length > 1 ? 's' : ''}: `}</span>
                <InnerHTML
                  tag="span"
                  className="rx-element-assignee"
                  html={document.citationPatents.map((patent) => patent.patentCountryCode).join(', ')}
                />
              </div>
            )}
            {isSinglePatent && <ViewFullPatentFamily metadata={metadata} document={document} viewFullPatentFamily={viewFullPatentFamily} />}
          </div>
          <DocumentButtons
            document={document}
            activeId={activeId}
            onToggle={handleToggle}
            onOpen={handleOpen}
            onClose={handleNavBlurClose}
            getTrackingAttrs={getTrackingAttrs}
            metadata={metadata}
            navRef={navRef}
          />
        </div>

        <DocumentCollapsibleContent
          metadata={metadata}
          document={document}
          zoom={metadata.isViewPreview ? 0 : zoom}
          activeId={activeId}
          onToggle={handleToggle}
          onClose={handleClose}
          getTrackingAttrs={getTrackingAttrs}
        />
      </div>
    </PatentContentContext.Provider>
  );
}

PatentContent.propTypes = {
  // Own props
  metadata: MetadataPropTypesShape,
  document: PropTypes.object.isRequired,
  zoom: PropTypes.number,
  // Set in parent
  isMainFamilyMember: PropTypes.bool,
  isSinglePatent: PropTypes.bool,
  getTrackingAttrs: PropTypes.func.isRequired,
  firstSectionExpanded: PropTypes.bool,
  setFirstSectionExpanded: PropTypes.func,
  viewFullPatentFamily: PropTypes.func,
};

export default PatentContent;
